// Signing in, on every page that asks who's visiting: Google's own button
// (their brand rules want it as is), and a quiet "sign out". The name-only
// sign-in is offered beside it only in local dev (the server says, in
// /api/visitors/me's sign_in). The server's copy of the button is
// plazmi.signin/button-html; keep the two alike.

export type SignIn = { google?: string | null; email?: string | null; name?: boolean };

const G = '<svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">'
  + '<path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>'
  + '<path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>'
  + '<path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>'
  + '<path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>';

const CSS = `
.gsi{display:inline-flex;align-items:center;gap:10px;height:40px;box-sizing:border-box;padding:0 12px;background:#fff;color:#1f1f1f !important;border:1px solid #747775;border-radius:4px;font:500 14px/1 Roboto,Arial,system-ui,sans-serif;letter-spacing:.25px;text-decoration:none;white-space:nowrap;cursor:pointer;vertical-align:middle;pointer-events:auto}
.gsi:hover{background:#f8f9fa;box-shadow:0 1px 2px rgba(60,64,67,.3)} .gsi:focus-visible{outline:2px solid #1a73e8;outline-offset:2px}
.gsi,.gsi span{font-weight:500 !important}
.gsi svg{flex:none} @media (pointer:coarse){.gsi{height:44px}}
.pz-say .gsi{align-self:flex-start;width:auto;margin:6px 0 2px}
.pz-gsi-row{display:block;margin-top:10px}
.pz-signout{background:none !important;border:none !important;box-shadow:none !important;padding:0 !important;margin-left:4px;font:inherit;font-weight:400;font-size:12.5px;color:var(--pz-ink-2,#6e5a48);text-decoration:underline;cursor:pointer;pointer-events:auto}
.pz-or{display:block;margin:8px 0 2px;font-size:12.5px;color:var(--pz-ink-2,#6e5a48)}
.pz-email{display:inline-block;margin:6px 0 2px;font-size:12.5px;font-weight:700;color:var(--pz-ink-2,#6e5a48);text-decoration:underline;text-underline-offset:2px;pointer-events:auto}
@media (pointer:coarse){.pz-email{padding:12px 0;margin:0}}`;

let styled = false;
function styleOnce() {
  if (styled) return;
  styled = true;
  const s = document.createElement('style'); s.textContent = CSS; document.head.appendChild(s);
}

const here = () => location.pathname + location.search;

/** Where Google's sign-in starts, coming back to this page. */
export const googleHref = (start = '/auth/google', back = here()) => `${start}?back=${encodeURIComponent(back)}`;

/** Google's standard "Sign in with Google" button. */
export function googleButton(start = '/auth/google', back = here()): HTMLAnchorElement {
  styleOnce();
  const a = document.createElement('a');
  a.className = 'gsi'; a.href = googleHref(start, back);
  a.innerHTML = `${G}<span>Sign in with Google</span>`;
  return a;
}

/** (M1) "or email me a sign-in link": to the server's page for it, coming back here after. */
export function emailLink(start = '/auth/email', back = here()): HTMLAnchorElement {
  styleOnce();
  const a = document.createElement('a');
  a.className = 'pz-email'; a.href = `${start}?back=${encodeURIComponent(back)}`;
  a.textContent = 'or email me a sign-in link';
  return a;
}

/** A quiet "sign out" for this browser; then `after` (by default, the page again). */
export function signOutButton(after: () => void = () => location.reload()): HTMLButtonElement {
  styleOnce();
  const b = document.createElement('button');
  b.type = 'button'; b.className = 'pz-signout'; b.textContent = 'sign out';
  b.onclick = async (e) => {
    e.preventDefault(); e.stopPropagation();
    await fetch('/auth/signout', { method: 'POST', headers: { accept: 'application/json' } }).catch(() => null);
    after();
  };
  return b;
}

/** A small "or just a name" line, when both ways are on offer. */
export function orJustAName(): HTMLElement {
  styleOnce();
  const s = document.createElement('span'); s.className = 'pz-or'; s.textContent = 'or just a name (local only)';
  return s;
}

/** Is the name-only sign-in on offer? (Yes until the server says otherwise.) */
export const nameOk = (o?: SignIn | null) => !o || o.name !== false;
